import "@voicerun/design-system/dist/index.css";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { StyleGuide } from "@voicerun/design-system";

import { store } from "./redux";
import { ThemeProvider } from "./contexts/ThemeContext";

import { AdminLayout } from "./layouts/AdminLayout";
import { AgentDetailLayout } from "./layouts/AgentDetailLayout";
import { AppLayout } from "./layouts/AppLayout";

import { AdminLeadsPage } from "./pages/admin/AdminLeadsPage";
import { AdminModelPricingPage } from "./pages/admin/AdminModelPricingPage";
import { AdminOrganizationDetailPage } from "./pages/admin/AdminOrganizationDetailPage";
import { AdminOrganizationsPage } from "./pages/admin/AdminOrganizationsPage";
import { AdminOverviewPage } from "./pages/admin/AdminOverviewPage";
import { AdminTemplatesPage } from "./pages/admin/AdminTemplatesPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminFineTuningDatasetsPage } from "./pages/admin/AdminFineTuningDatasetsPage";
import { AdminFineTuningJobsPage } from "./pages/admin/AdminFineTuningJobsPage";
import { AdminFineTuningEvalsPage } from "./pages/admin/AdminFineTuningEvalsPage";
import { AdminFineTuningDeploymentsPage } from "./pages/admin/AdminFineTuningDeploymentsPage";
import { AdminFineTuningModelsPage } from "./pages/admin/AdminFineTuningModelsPage";
import { AdminServicePrincipalsPage } from "./pages/admin/AdminServicePrincipalsPage";
import { AdminBugTicketsPage } from "./pages/admin/AdminBugTicketsPage";
import { BugReportsPage } from "./pages/BugReportsPage";
import { BugReportDetailPage } from "./pages/BugReportDetailPage";
import { AdminCoderunnerPodsPage } from "./pages/admin/AdminCoderunnerPodsPage";
import { AgentDetailsPage } from "./pages/agent/AgentDetailsPage";
import { AgentDeploymentsPage } from "./pages/agent/AgentDeploymentsPage";
import { AgentReleasesPage } from "./pages/agent/AgentReleasesPage";
import { AgentFunctionPage } from "./pages/agent/AgentFunctionPage";
import { AgentsPage } from "./pages/agent/AgentsPage";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { VoiceLabPage } from "./pages/VoiceLabPage";
import { InviteAcceptPage } from "./pages/InviteAcceptPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OrganizationPage } from "./pages/OrganizationPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SignInPage } from "./pages/SignInPage";
import { SignOutPage } from "./pages/SignOutPage";
import { SignUpPage } from "./pages/SignUpPage";
import { AgentEnvironmentsPage } from "./pages/agent/AgentEnvironmentsPage";
import { AgentSessionsPage } from "./pages/agent/AgentSessionsPage";
import { MissionControlPage } from "./pages/MissionControlPage";
import { StandardLayout } from "./layouts/StandardLayout";
import { CliLoginPage } from "./pages/CliLoginPage";
import { AgentExperimentsPage } from "./pages/agent/AgentExperimentsPage";
import { AgentEvaluationPage } from "./pages/agent/AgentEvaluationPage";
import { AgentEvaluationEvaluateTab } from "./pages/agent/AgentEvaluationEvaluateTab";
import { AgentEvaluationEvaluationsTab } from "./pages/agent/AgentEvaluationEvaluationsTab";
import { AgentCustomMetricsPage } from "./pages/agent/AgentCustomMetricsPage";
import { ResourcesPage } from "./pages/ResourcesPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { NotificationPreferencesPage } from "./pages/NotificationPreferencesPage";
import { TranscribePage } from "./pages/TranscribePage";
import { LLMLabPage } from "./pages/LLMLabPage";
import { DemoSharePage } from "./pages/DemoSharePage";

export const App = () => {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public, no-login client demo pages. Mounted OUTSIDE AppLayout so
                it never loads a session or app chrome. */}
            <Route
              path="/demo/:slug"
              element={<DemoSharePage />}
            />
            <Route
              path="/"
              element={<AppLayout />}
            >
              <Route
                path="/signin"
                element={<SignInPage />}
              />
              <Route
                path="/signup"
                element={<SignUpPage />}
              />
              <Route
                path="/signout"
                element={<SignOutPage />}
              />
              <Route
                path="/style-guide"
                element={<StyleGuide />}
              />
              <Route
                path="/cli-login"
                element={<CliLoginPage />}
              />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/invites/:inviteId/accept"
                element={<InviteAcceptPage />}
              />
              <Route
                path="/"
                element={<StandardLayout />}
              >
                <Route
                  path="/"
                  element={<MissionControlPage />}
                />
                <Route
                  path="/profile"
                  element={<ProfilePage />}
                />
                <Route
                  path="/notifications"
                  element={<NotificationsPage />}
                />
                <Route
                  path="/notification-preferences"
                  element={<NotificationPreferencesPage />}
                />
                <Route
                  path="/bug-reports"
                  element={<BugReportsPage />}
                />
                <Route
                  path="/bug-reports/:ticketId"
                  element={<BugReportDetailPage />}
                />
                <Route
                  path="/organization"
                  element={
                    <Navigate
                      to="/organization/members"
                      replace
                    />
                  }
                />
                <Route
                  path="/organization/members"
                  element={<OrganizationPage />}
                />
                <Route
                  path="/organization/configurations"
                  element={<OrganizationPage />}
                />
                <Route
                  path="/organization/usage"
                  element={<OrganizationPage />}
                />
                <Route
                  path="/organization/billing"
                  element={<OrganizationPage />}
                />
                <Route
                  path="/resources"
                  element={<ResourcesPage />}
                />
                <Route
                  path="/voice-lab"
                  element={<VoiceLabPage />}
                />
                <Route
                  path="/sttlab"
                  element={<TranscribePage />}
                />
                <Route
                  path="/llm-lab"
                  element={<LLMLabPage />}
                />
                <Route
                  path="/agents"
                  element={<AgentsPage />}
                />
                <Route
                  path="/agents/new"
                  element={<AgentFunctionPage />}
                />
                <Route
                  path="/agents/:agentId"
                  element={<AgentDetailLayout />}
                >
                  <Route
                    index
                    element={
                      <Navigate
                        to="overview"
                        replace
                      />
                    }
                  />
                  <Route
                    path="overview"
                    element={<AgentDetailsPage />}
                  />
                  <Route
                    path="environments"
                    element={<AgentEnvironmentsPage />}
                  />
                  <Route
                    path="function"
                    element={<AgentFunctionPage />}
                  />
                  <Route
                    path="sessions"
                    element={<AgentSessionsPage />}
                  />
                  <Route
                    path="deployments"
                    element={<AgentDeploymentsPage />}
                  />
                  <Route
                    path="releases"
                    element={<AgentReleasesPage />}
                  />
                  <Route
                    path="evaluation"
                    element={<AgentEvaluationPage />}
                  >
                    <Route
                      index
                      element={
                        <Navigate
                          to="evaluations"
                          replace
                        />
                      }
                    />
                    <Route
                      path="evaluations"
                      element={<AgentEvaluationEvaluationsTab />}
                    />
                    <Route
                      path="evaluate"
                      element={<AgentEvaluationEvaluateTab />}
                    />
                  </Route>
                  <Route
                    path="experiments"
                    element={<AgentExperimentsPage />}
                  />
                  <Route
                    path="metrics"
                    element={<AgentCustomMetricsPage />}
                  />
                </Route>
              </Route>

              <Route
                path="/admin"
                element={<AdminLayout />}
              >
                <Route
                  path="/admin"
                  element={<AdminOverviewPage />}
                />
                <Route
                  path="/admin/leads"
                  element={<AdminLeadsPage />}
                />
                <Route
                  path="/admin/users"
                  element={<AdminUsersPage />}
                />
                <Route
                  path="/admin/organizations"
                  element={<AdminOrganizationsPage />}
                />
                <Route
                  path="/admin/organizations/:organizationId"
                  element={<AdminOrganizationDetailPage />}
                />
                <Route
                  path="/admin/model-pricing"
                  element={<AdminModelPricingPage />}
                />
                <Route
                  path="/admin/templates"
                  element={<AdminTemplatesPage />}
                />
                <Route
                  path="/admin/fine-tuning-datasets"
                  element={<AdminFineTuningDatasetsPage />}
                />
                <Route
                  path="/admin/fine-tuning-jobs"
                  element={<AdminFineTuningJobsPage />}
                />
                <Route
                  path="/admin/fine-tuning-deployments"
                  element={<AdminFineTuningDeploymentsPage />}
                />
                <Route
                  path="/admin/fine-tuning-evals"
                  element={<AdminFineTuningEvalsPage />}
                />
                <Route
                  path="/admin/fine-tuning-models"
                  element={<AdminFineTuningModelsPage />}
                />
                <Route
                  path="/admin/service-principals"
                  element={<AdminServicePrincipalsPage />}
                />
                <Route
                  path="/admin/bug-tickets"
                  element={<AdminBugTicketsPage />}
                />
                <Route
                  path="/admin/coderunner"
                  element={<AdminCoderunnerPodsPage />}
                />
              </Route>
              <Route
                path="*"
                element={<NotFoundPage />}
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
};
